import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import menuitems from '../menuitems.js';
import RoleService from '../Services/roleService';
import { getActiveButtons } from '../redux/selectors';
import { navbutton_click, submenubutton_click } from '../redux/actions';
import Content from '../content.js';
import SystemAlertPopup from '../Elements/SystemAlertPopup.js';
import NavRailIcon from './NavRailIcon';
import NavFlyout from './NavFlyout';
import NavSearchFlyout from './NavSearchFlyout';
import ProfileRailTrigger from './ProfileRailTrigger';
import ProfileMenuPopover from './ProfileMenuPopover';
import ExpandedNav from './ExpandedNav';
import useIsMobile from '../Helpers/useIsMobile';

// Bravo navigation rail. Drop-in replacement for navbar.js: same props,
// still renders <Content /> as a flex sibling.
export default function BravoSidebar(props) {
  const { loggedInUser, loggedInUsersNotInUse, handleLogoutClick, handleSwitchUserClick, switchToUserClick } = props;
  const activeButtons = useSelector(getActiveButtons);
  const dispatch = useDispatch();

  // Single open overlay: {type:'flyout', index} | {type:'search'} | {type:'profile'} | null.
  const [overlay, setOverlay] = useState(null);
  // Mobile (<=768px) off-canvas drawer state; inert on desktop (rail always shown).
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Desktop pin: the rail widens to a labelled 256px menu that pushes content.
  // Persisted so the choice survives reloads. Dormant on mobile (drawer always
  // shows the rail).
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(() => {
    try {
      return localStorage.getItem('bravo-sidebar') === 'expanded';
    } catch (e) {
      return false;
    }
  });
  const [query, setQuery] = useState('');
  // Which section's inline sub-menu is open (single-open). Default: the active
  // section, if it is a group.
  const [openGroup, setOpenGroup] = useState(() => {
    const nav = activeButtons.nav;
    const sec = menuitems[nav];
    return sec && sec.submenu && sec.submenu.length > 0 ? nav : null;
  });
  // On a deep link the active section resolves only after mount (content.js maps the
  // URL and dispatches link_click), so the seed above still sees the redux default
  // (CRM). Follow the active section whenever it changes; manual toggling is untouched
  // because it never changes activeButtons.nav.
  useEffect(() => {
    const sec = menuitems[activeButtons.nav];
    if (sec && sec.submenu && sec.submenu.length > 0) setOpenGroup(activeButtons.nav);
  }, [activeButtons.nav]);

  const full = !isMobile && expanded;

  const pin = (v) => {
    setExpanded(v);
    try {
      localStorage.setItem('bravo-sidebar', v ? 'expanded' : 'collapsed');
    } catch (e) {}
    if (v) setOverlay(null); // close any open flyout / search / profile when pinning
  };

  // Hover-to-open: pending close timer so the cursor can travel the gap between
  // the rail and the flyout without the flyout snapping shut.
  const closeTimer = useRef(null);
  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => {
      setOverlay((prev) => (prev && prev.type === 'flyout' ? null : prev));
    }, 180);
  };
  useEffect(() => cancelClose, []);

  // Hovering an icon opens its flyout immediately (so moving down the rail opens
  // each section in succession); hovering a section without a submenu closes it.
  const handleIconHover = (item, index) => {
    cancelClose();
    if (item.submenu && item.submenu.length > 0) {
      setOverlay({ type: 'flyout', index });
    } else {
      setOverlay((prev) => (prev && prev.type === 'flyout' ? null : prev));
    }
  };

  // Close everything after a navigation so the mobile drawer doesn't linger over content.
  const closeAll = () => {
    cancelClose();
    setOverlay(null);
    setDrawerOpen(false);
  };

  const stageBack =
    process.env.REACT_APP_HOST_ENV === 'staging' || process.env.REACT_APP_HOST_ENV === 'UAT' ? 'bg-staging' : 'bg-prod';

  const visibleSections = [];
  for (let i = 0; i < menuitems.length; i++) {
    const item = menuitems[i];
    if (item.visible === false) continue;
    if (item.shown === false) continue;
    if (!RoleService.CheckUserPermission('menu', item.key)) continue;
    visibleSections.push({ item, index: i });
  }

  // Logo click → Home. The home section's own `link` is a placeholder string,
  // so navigate via its first submenu item (the Home page), mirroring a flyout pick.
  const goHome = () => {
    const homeIndex = menuitems.findIndex((m) => m.url === 'home');
    if (homeIndex < 0) return;
    const section = menuitems[homeIndex];
    const homeItem = (section.submenu && section.submenu[0]) || section;
    dispatch(navbutton_click(homeIndex, section.link, section.text, section.linktext, section.icon, section.url));
    dispatch(
      submenubutton_click(
        0,
        homeItem.link,
        homeItem.linktext ? homeItem.linktext : homeItem.text,
        homeItem.icon,
        homeItem.url,
        homeIndex
      )
    );
    closeAll();
  };

  const handleIconClick = (item, index) => {
    if (item.submenu && item.submenu.length > 0) {
      setOverlay((prev) => (prev && prev.type === 'flyout' && prev.index === index ? null : { type: 'flyout', index }));
    } else {
      dispatch(navbutton_click(index, item.link, item.text, item.linktext, item.icon, item.url));
      closeAll();
    }
  };

  const flyoutOpen = overlay && overlay.type === 'flyout';

  return (
    <div className={`wrapper ${stageBack}`}>
      <div className={`bravo-ds${drawerOpen ? ' nav-open' : ''}${full ? ' nav-expanded' : ''}`}>
        <button
          type='button'
          className='nav-burger'
          aria-label='Menu'
          onClick={() => setDrawerOpen((o) => !o)}
        >
          <i className='fa-solid fa-bars' />
        </button>
        <div
          className='nav-backdrop'
          onClick={closeAll}
        />
        <nav
          className={`sidebar${full ? ' expanded' : ''}`}
          onMouseLeave={full ? undefined : scheduleClose}
        >
          {full ? (
            <ExpandedNav
              visibleSections={visibleSections}
              activeButtons={activeButtons}
              openGroup={openGroup}
              setOpenGroup={setOpenGroup}
              query={query}
              setQuery={setQuery}
              onNavigate={closeAll}
              onCollapse={() => pin(false)}
              goHome={goHome}
              loggedInUser={loggedInUser}
              profileActive={overlay && overlay.type === 'profile'}
              onProfileToggle={() =>
                setOverlay((prev) => (prev && prev.type === 'profile' ? null : { type: 'profile' }))
              }
            />
          ) : (
            <>
              <button
                type='button'
                className='brand'
                onClick={goHome}
                aria-label='Home'
                title='Home'
              >
                <img
                  src='/assets/img/brand/bare-icon.png'
                  alt='Bravo'
                />
              </button>

              <button
                type='button'
                className={`nav-ico${overlay && overlay.type === 'search' ? ' active' : ''}`}
                title='Search menu'
                aria-label='Search menu'
                onClick={() => setOverlay((prev) => (prev && prev.type === 'search' ? null : { type: 'search' }))}
                onMouseEnter={cancelClose}
              >
                <i className='fa-solid fa-magnifying-glass' />
              </button>

              {visibleSections.map(({ item, index }) => (
                <NavRailIcon
                  key={`nav-ico-${index}`}
                  icon={item.icon}
                  title={item.text}
                  active={index === activeButtons.nav}
                  onClick={() => handleIconClick(item, index)}
                  onMouseEnter={() => handleIconHover(item, index)}
                />
              ))}

              <div className='sp' />

              {!isMobile && (
                <button
                  type='button'
                  className='nav-ico pin'
                  title='Open sidebar'
                  aria-label='Open sidebar'
                  onClick={() => pin(true)}
                  onMouseEnter={cancelClose}
                >
                  <i className='fa-solid fa-table-columns' />
                </button>
              )}

              <ProfileRailTrigger
                loggedInUser={loggedInUser}
                active={overlay && overlay.type === 'profile'}
                onToggle={() => setOverlay((prev) => (prev && prev.type === 'profile' ? null : { type: 'profile' }))}
              />
            </>
          )}
        </nav>

        {!full && flyoutOpen && (
          <NavFlyout
            section={menuitems[overlay.index]}
            sectionIndex={overlay.index}
            activeSub={activeButtons.sub}
            isActiveSection={overlay.index === activeButtons.nav}
            onClose={() => setOverlay(null)}
            onNavigate={closeAll}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
          />
        )}

        {!full && overlay && overlay.type === 'search' && (
          <NavSearchFlyout
            onClose={() => setOverlay(null)}
            onNavigate={closeAll}
          />
        )}

        <ProfileMenuPopover
          open={overlay && overlay.type === 'profile'}
          onClose={() => setOverlay(null)}
          onNavigate={closeAll}
          shifted={full}
          loggedInUser={loggedInUser}
          loggedInUsersNotInUse={loggedInUsersNotInUse}
          switchToUserClick={switchToUserClick}
          handleSwitchUserClick={handleSwitchUserClick}
          handleLogoutClick={handleLogoutClick}
        />
      </div>

      <Content />
      <SystemAlertPopup />
    </div>
  );
}
